import { makeSubject, map, mergeMap, pipe, scan, share, tap } from 'wonka';

import { AdvertPayload } from './@types/ad-payload';
import { TagCommand } from './@types/commands';
import { PageOptions } from './@types/page-options';
import { loadAdvert$ } from './advert';
import { debugPluginNewTag } from './debug';
import { getEnvironment, getOptions } from './environment';
import { lifecycle } from './lifecycle';
import { logError } from './logger';
import { blockIfDoubleVerify$ } from './third-party/double-verify';
import { RE_COPPA, RE_GDPR, RE_GDPR_CONSENT } from './third-party/gdpr';
import {
  createMobkoiContainer,
  isDOMNode,
  restoreTouchedElementsToOriginalState,
} from './utils/dom';
import { deepMerge, omit } from './utils/object';
import { buster } from './utils/util';

/**
 * This is only a class as there is some mutation that goes on over the lifecycle
 * of the tag, and also a central place to store info. The main thing that gets
 * mutated over the flow is the container - this is where we load our ad to (DOM
 * mutation galore), and pageOptions gets built at the first stage (more of a merge)
 * We also store an array of cleanup functions that should be called if the
 * ad gets destroyed instead of storing the implementations directly on the tag,
 * we can just hold a reference to callbacks to do what they need to do without
 * unnecessarily bloating out this class.
 * This is also the main entity that gets passed through all the streams unless
 * otherwise specified
 */
export class Tag {
  _id: string;
  tagId: string;
  target: HTMLOrSVGScriptElement | string;
  pageOptions: PageOptions;
  config: Partial<PageOptions>;
  auctionDetails: Record<string, unknown> | null;
  // The main container on the page, we set general sizing rules on this to push
  // publisher content and accommodate most creatives. Relatively positioned.
  container: HTMLElement | null = null;
  // Nested in the container, this is where we render our creative, may break out of
  // the parent container - absolubtely positioned
  creativeContainer: HTMLElement | null = null;
  payload: AdvertPayload | null = null;
  cleanupFunctions: (() => void)[] = [];
  destroyed = false;

  constructor({ target, tagId, page, config }: TagCommand) {
    this._id = buster();
    this.tagId = tagId;
    this.target = target;
    this.pageOptions = page;
    // Set these here so they're never undefined down the chain
    this.pageOptions.constraints = this.pageOptions.constraints || {};
    this.pageOptions.macros = this.pageOptions.macros || {};
    this.config = config;
    this.auctionDetails = config.aud || null;
  }

  destroy() {
    this.destroyed = true;
    this.cleanupFunctions.forEach((cleanup) => cleanup());
    restoreTouchedElementsToOriginalState();
    try {
      this.container?.parentNode?.removeChild(this.container);
      this.container = null;
    } catch (e) {
      logError({
        tagId: this.tagId,
        reason: 'lib-err',
        detail: (e as Error).stack || e,
        error: (e as Error).stack || e,
        impressionId: this.pageOptions.iid || '',
      });
    }
  }
}

const setPageOptions = (tag: Tag) => {
  const options = getOptions();
  const pageOptions = tag.pageOptions;
  //  if the publisher passes us the consent macro literals instead of the values then
  //  delete them, and we'll use whatever is in SHARED_OPTIONS.
  const optionsToOmit = {
    gdpr: RE_GDPR,
    gdpr_consent: RE_GDPR_CONSENT,
    coppa: RE_COPPA,
  };

  const keysToOmit = Object.entries(optionsToOmit)
    .map(([key, regex]) =>
      regex.test(pageOptions[key as keyof typeof optionsToOmit]) ? key : '',
    )
    .filter((key): key is keyof PageOptions => !!key);

  // Merge tag options over global options
  tag.pageOptions = deepMerge<PageOptions>(
    {},
    options,
    omit(keysToOmit, pageOptions),
  );

  return tag;
};

export const createContainer = (tag: Tag) => {
  const environment = getEnvironment();
  const {
    target,
    pageOptions: { constraints },
  } = tag;
  //  Before we load the advert we first need to figure out how big an ad we can load (width x height), and to
  //  do that we need to add our container at the right place.
  let containerSibling;

  //  we're in a friendly iframe so instead of dropping our container in the iframe we'll instead break out
  //  and add as a sibling. If we didn't then on window.resize() we'd resize our container in the iframe
  //  correctly, but Celtra _would_ break out and not respond to the resize. So...if we can break out then
  //  we may as well.
  //  Also, since we're not really using the iframe anymore, hide it so it doesn't take up any space.
  if (environment.frameElement && !environment.isEnsighten) {
    containerSibling = environment.frameElement;
    containerSibling.style.display = 'none';
  } else if (isDOMNode(target)) {
    //  not in an iframe and our target element is an actual dom node (which must be in the current document)
    containerSibling = target;
  } else if (!containerSibling && typeof target === 'string') {
    //  assume target is a css selector string, so try to find it in the active document.
    containerSibling = (
      environment.frameElement
        ? environment.frameElement.ownerDocument
        : document
    ).querySelector(target);
  }

  // No container available, destroy ourselves
  if (!containerSibling) {
    tag.destroy();
    return tag;
  }

  // Create the container, render it and save it to the tag instance
  tag.container = createMobkoiContainer(
    '',
    containerSibling.ownerDocument,
    constraints.padding || 0,
    { display: 'none' },
  );

  // We create a creative container to hold the ad markup so we can resize it to
  // full width without affecting the parent container/ page layout as it can break out
  // @see https://flexibleads.iabtechlab.com/src/slot_definition_techniques.html
  tag.creativeContainer = createMobkoiContainer(
    '',
    containerSibling.ownerDocument,
    0,
    {
      width: '100%',
      height: '100%',
    },
  );

  // Append creative container to the main container
  tag.container.appendChild(tag.creativeContainer);

  // insert our container to the dom
  containerSibling.parentNode!.insertBefore(
    tag.container,
    containerSibling.nextSibling,
  );

  return tag;
};

const tagSubject$ = makeSubject<Tag>();

/**
 * Hold all tags in the session, these accumulate over time if
 * there are multiple tags loaded to the library.
 * Setup tags in flow. Ensure that tag is passed from one operator
 * to the next (unless destroyed) so that the final `scan` (AKA `reduce`) holds all
 * our built tags in this session
 */
export const tags$ = pipe(
  tagSubject$.source,
  tap(debugPluginNewTag),
  map(setPageOptions),
  map(createContainer),
  mergeMap(blockIfDoubleVerify$),
  mergeMap(loadAdvert$),
  scan<Tag, Tag[]>((acc, tag) => [...acc, tag], []),
  // We only want to have once instance of tag$ stream, "share" it so
  // that it doesn't get re-initialised on multiple subscriptions and lead
  // to duplicate tags/ads on the page. That would be bad.
  share,
);

// Create our tag instance and throw it into the tag$ stream via the subject
export const handleTag = (tag: TagCommand) => {
  tagSubject$.next(new Tag(tag));
};
